import {
  Bell,
  Briefcase,
  Building2,
  Cake,
  CalendarDays,
  CircleDollarSign,
  ClipboardList,
  ConciergeBell,
  CreditCard,
  FileText,
  History as HistoryIcon,
  Home,
  MessageSquare,
  Palette,
  Repeat,
  ShieldCheck,
  SlidersHorizontal,
  Star,
  Users,
  UsersRound,
  Video,
  Wallet,
  Wrench,
  AudioLines,
  BookOpen,
  PhoneForwarded,
  Sparkles,
  type LucideIcon,
} from 'lucide-react'

/**
 * Canonical page title map used by the PageHeaderBar and the usePageTitle hook.
 * Keys are route pathnames; values are human-readable page names (without suffix).
 */
export const pageTitles: Record<string, string> = {
  '/home': 'Home',
  '/setup/business': 'Finish setup',
  // The route keeps its old name; the page is "Needs your attention" everywhere
  // a person reads it (phones shorten it to "Attention", see pageShortTitles).
  '/queue': 'Needs your attention',
  '/aria': 'Brain',
  '/messages': 'Messages',
  '/calls': 'Calls',
  '/money': 'Money',
  '/customers-v2': 'Customers',
  '/settings': 'Business profile',
  '/settings/overview': 'Settings',
  '/settings/phone-assistant': 'Phone assistant',
  '/settings/call-handling': 'Call handling',
  '/settings/invoices': 'Invoices',
  '/settings/services': 'Services',
  '/settings/memory': 'Memory',
  '/reports': 'Reports',
  '/notifications': 'Activity',
  '/settings/payments': 'Payments',
  '/settings/billing': 'Ten Four billing',
  // "Preferences" everywhere — matches the sidebar label for this page (DES-28).
  '/settings/preferences': 'Preferences',
  '/invoices': 'Invoices',
  // '/customers' stays for the legacy-alias detail mount (/customers/:id);
  // the /customers list itself now redirects to /customers-v2.
  '/customers': 'Customers',
  '/jobs': 'Jobs',
  '/history': 'History',
  '/forms': 'Forms',
  '/reminders': 'Reminders',
  '/calendar': 'Calendar',
  // Meetings keeps the Lucide glyph for search/palette, while PageHeaderIcon
  // supplies its approved clay destination mark. It still has no rail entry.
  '/meetings': 'Meetings',
  '/calendar/meetings': 'Meetings',
  // One term per concept (DES-28): the merged inbox is "Messages" on both mounts.
  '/conversations': 'Messages',
  '/settings/team': 'Team',
  '/settings/security': 'Security',
  '/settings/birthdays': 'Birthday texts',
  '/settings/reviews': 'Review follow-ups',
  '/settings/subscriptions': 'Subscriptions',
}

/**
 * Resolves a pathname to its page title, handling detail routes
 * (/invoices/:id, /jobs/:id, /conversations/:id) by section. Shared by the
 * document-title hook and the mobile app bar. Returns null when unknown.
 */
export function resolvePageTitle(pathname: string): string | null {
  if (pathname.startsWith('/invoices/') && pathname !== '/invoices') {
    return 'Invoice'
  }
  // Meetings live under /calendar (#536) — the detail route must not fall
  // back to "Calendar".
  if (pathname.startsWith('/calendar/meetings')) {
    return 'Meetings'
  }
  if (pathname.startsWith('/forms/') && pathname !== '/forms') {
    return 'Form'
  }
  const exact = pageTitles[pathname]
  if (exact) return exact
  // Detail routes fall back to their section (/jobs/:id → Jobs).
  return pageTitles['/' + pathname.split('/')[1]] ?? null
}

/**
 * Phone-width names for titles too long for the ≤768px header and the bottom
 * tab bar. The full `pageTitles` name stays the document title and what a
 * screen reader hears.
 */
export const pageShortTitles: Record<string, string> = {
  '/queue': 'Attention',
}

/** The phone-width name for a route, when it has one. */
export function resolvePageShortTitle(pathname: string): string | undefined {
  return pageShortTitles[pathname]
}


/**
 * Per-route Lucide glyph for the command palette / search results. The shell
 * chrome (PageHeaderBar, sidebar, bottom tab bar) uses clay `PageHeaderIcon`
 * instead. Keys match `pageTitles`.
 */
export const pageIcons: Record<string, LucideIcon> = {
  '/home': Home,
  '/queue': ConciergeBell,
  '/aria': Sparkles,
  '/messages': MessageSquare,
  '/calls': PhoneForwarded,
  '/conversations': MessageSquare,
  '/money': Wallet,
  '/customers-v2': Users,
  '/customers': Users,
  '/jobs': Briefcase,
  '/history': HistoryIcon,
  '/forms': ClipboardList,
  '/invoices': CircleDollarSign,
  '/reminders': Bell,
  '/calendar': CalendarDays,
  '/meetings': Video,
  '/reports': FileText,
  '/notifications': Bell,
  '/settings': Building2,
  '/settings/invoices': FileText,
  '/settings/phone-assistant': AudioLines,
  '/settings/call-handling': PhoneForwarded,
  '/settings/services': Wrench,
  '/settings/memory': BookOpen,
  '/settings/payments': CreditCard,
  '/settings/preferences': SlidersHorizontal,
  '/settings/birthdays': Cake,
  '/settings/reviews': Star,
  '/settings/subscriptions': Repeat,
  '/settings/team': UsersRound,
  '/settings/security': ShieldCheck,
}

/**
 * Resolves a pathname to its header glyph, using the same exact-then-section
 * fallback as `resolvePageTitle`. Returns null when the route has no icon, so
 * the header renders the title alone rather than a placeholder.
 */
export function resolvePageIcon(pathname: string): LucideIcon | null {
  const exact = pageIcons[pathname]
  if (exact) return exact
  return pageIcons['/' + pathname.split('/')[1]] ?? null
}
